import { UtilitiesService } from '@toro/common/shared/services/utilities.service';
import { DialogsService } from '@toro/common/shared/services/dialogs.service';
import { UtilsAction } from '@toro/common/shared/state-management/utils/utils.actions';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { Subscription, ReplaySubject } from 'rxjs';
import { OrderMarketTypeEnum } from '@toro/common/shared/enum/orders/market-type.enum';
import { Observable } from 'rxjs/Observable';
import { ExchangeActions } from '../../../state-management/exchange/exchange.action';
import { DateFormatService } from '@toro/common/shared/services/date-format/date-format.service';
import { DatePipe } from '@angular/common';
import { SuccessActionResultComponent } from '../checkout/dialogs/success-action-result/success-action-result.component';
import { interval } from 'rxjs/observable/interval';
import { merge } from 'rxjs/operator/merge';
import { TextUtils } from '@toro/common/shared/resources/text.utils';
import { TrackingActions } from '@toro/common/shared/state-management/tracking/tracking.action';
import { MarketClosedWarningEvent } from '@toro/common/shared/models/segment/market-closed-warning-event.model';
import { SegmentEvent } from '@toro/common/shared/enum/segment/segment-event.enum';

@Injectable()
export class StocksMarketInteractionsService {

    private tradingTimeSubscription: Subscription;
    /**
     * Hora do signalR
     */
    private time$ = this.store.select((state) => state.utils.timer);
    /**
     * Horários de abertura e fechamento dos mercados
     */
    private trandingTime$ = this.store.select((state) => state.exchange.tradingTime)
        .filter((state) => state !== null);
    /**
     * Hora local, emitida apenas quando não chegou hora do signal (delay de 4 segundos)
     */
    private localTime$ = Observable.interval(1000)
        .withLatestFrom(this.time$)
        .filter(([index, time]) => !time)
        .map(([index, time]) => this.getLocalTime())
        .shareReplay();

    private now$ = this.time$.merge(this.localTime$)
        .filter((time) => !!time);

    constructor(
        private store: Store<AppState>,
        private dateFormat: DateFormatService,
        private dialogs: DialogsService,
        private datePipe: DatePipe,
        private text: TextUtils,
        private utilities: UtilitiesService,
    ) {
        // Informações de aberta e fechamento dos mercados
        this.store.dispatch({
            type: ExchangeActions.getTradingTime,
        });
        // Hora atualizada
        this.store.dispatch({
            type: UtilsAction.getTimer,
        });
    }

    public ngOnDestroy() {
        if (this.tradingTimeSubscription) this.tradingTimeSubscription.unsubscribe();
    }

    /**
     * Exibir recibo de ordem de encerramento agendada
     * @param openingTime horário de abertura do mercado ao qual a ordem foi enviada (HH:mm)
     */
    public showClosingOrderBookedReceipt(openingTime: string) {
        this.dialogs.showComponnent(SuccessActionResultComponent, {
            message: `A sua ordem de encerramento foi agendada com sucesso e será processada a partir das ${openingTime} do próximo dia útil. 
                        Você pode acompanhar esta ordem e conferir se este investimento foi encerrado na área de Meus Investimentos.`,
            title: `Ordem de encerramento agendada com sucesso.`,
            icon: 'waiting',
        });
    }

    /**
     * Exibir recibo de ordem agendada
     * @param openingTime horário de abertura do mercado ao qual a ordem foi enviada (HH:mm)
     */
    public showOrderBookedReceipt(openingTime: string, buy: boolean) {
        const operation = buy ? 'compra' : 'venda';
        const operationPast = buy ? 'comprado' : 'vendido';

        this.dialogs.showComponnent(SuccessActionResultComponent, {
            message: `A sua ordem de ${operation} foi agendada com sucesso e será processada a partir das ${openingTime} do próximo dia útil. 
                    Você pode acompanhar esta ordem e conferir se este investimento foi ${operationPast} na área de Meus Investimentos.`,
            title: `Ordem de ${operation} agendada com sucesso.`,
            icon: 'waiting',
        });
    }

    /**
     * Tentativa de investimento. Se o mercado estiver fechado exibe modal e recupera confirmação do usuário
     * para agendar o investimento.
     * @param market mercado da operação
     */
    public onPlannedInvestimentAttempt(market: OrderMarketTypeEnum): Promise<Result> {

        return new Promise<{ market: { marketOpen: boolean, openingTime: string }, confirm: boolean }>((resolve) => {
            // Função para confirmar o investimento mesmo com o mercado fechado
            const confirmationFlow = (resolver: (resolver: Result) => void, openingDate: string, openingTime: string) => {
                this.confirmPlannedInvestmentBooking(openingDate)
                    .then((confirm) => {
                        resolve({
                            market: {
                                marketOpen: false,
                                openingTime,
                            },
                            confirm,
                        });
                    });
            };

            this.checkMarketStatus(
                market,
                confirmationFlow,
                (result: Result) => resolve(result),
            );
        });
    }



    /**
     * Tentativa de encerramento de um investimento planejado. 
     * Se o mercado estiver fechado exibe modal e recupera confirmação do usuário
     * para agendar o investimento.
     * @param market mercado da operação
     */
    public onPlannedInvestimentCloseAttempt(market: OrderMarketTypeEnum): Promise<Result> {

        return new Promise<{ market: { marketOpen: boolean, openingTime: string }, confirm: boolean }>((resolve) => {
            // Função para confirmar o investimento mesmo com o mercado fechado
            const confirmationFlow = (resolver: (resolver: Result) => void, openingDate: string, openingTime: string, closingTime?: string) => {
                this.closePlannedInvestmentBooking(openingDate, closingTime)
                    .then((confirm) => {
                        resolve({
                            market: {
                                marketOpen: false,
                                openingTime,
                            },
                            confirm: false,
                        });
                    });
            };

            this.checkMarketStatus(
                market,
                confirmationFlow,
                (result: Result) => resolve(result),
            );
        });
    }

    /**
     * Confirma agendamento de investimento não planejado
     * @param market mercado do investimento
     * @param buy compra?
     */
    public onInvestmentAttempt(market: OrderMarketTypeEnum, buy: boolean): Promise<Result> {
        return new Promise<{ market: { marketOpen: boolean, openingTime: string }, confirm: boolean }>((resolve) => {
            // Função para confirmar o investimento mesmo com o mercado fechado
            const confirmationFlow = (resolver: (resolver: Result) => void, openingDate: string, openingTime: string) => {
                this.confirmInvestmentBooking(openingDate, buy)
                    .then((confirm) => {
                        resolve({
                            market: {
                                marketOpen: false,
                                openingTime,
                            },
                            confirm,
                        });
                    });
            };

            this.checkMarketStatus(
                market,
                confirmationFlow,
                (result: Result) => resolve(result),
            );
        });
    }

    /**
     * Verifica status do mercado, caso esteja fechado invoca o callback para confirmar o agendamento do investimento
     * @param market mercado do investimento
     * @param confirmationCallback callback para ser invocado quando houver necessidade de confirmar o agendamento
     * @param resolve função para resolver o processo
     */
    private checkMarketStatus(
        market: OrderMarketTypeEnum,
        confirmationCallback: (resolver: (resolver: Result) => void, openingDate: string, openingTime: string, closingTime?: string) => void,
        resolve: (result: Result) => void,
    ) {
        this.trandingTime$.combineLatest(this.now$)
            .first()
            .subscribe(([tradingTime, now]) => {

                let opening;
                let closing;

                // Verifica o mercado
                if (market === OrderMarketTypeEnum.Future) {
                    opening = tradingTime.openingFuture;
                    closing = tradingTime.closingFuture;
                } else {
                    opening = tradingTime.openingBovespa;
                    closing = tradingTime.limitDayTradeStock;
                }

                // Extrair apenas horas
                const openingTime = this.dateFormat.extractTimeFromDateTime(opening);
                const closingTime = this.dateFormat.extractTimeFromDateTime(closing);
                const marketOpen = now >= openingTime && now <= closingTime;
                
                if (marketOpen) {
                    resolve({
                        market: {
                            marketOpen,
                            openingTime,
                        },
                        confirm: true,
                    });
                    return;
                }

                confirmationCallback(resolve, opening, openingTime, closingTime);
            });
    }

    /**
     * Confirmar agendamento de investimento planejado (mercado fechado)
     * @param opening abertura (data e hora em formato 2018-01-22 14:00)
     */
    private confirmPlannedInvestmentBooking(opening: string): Promise<boolean> {
        // Localiza para hora local
        const openingTime = this.datePipe.transform(opening, 'HH:mm');
        const openTime = new Date();

        return this.dialogs.confirmAction(
            'O mercado está fechado.',
            `Se agendar seu investimento agora, ele será processado a partir das ${openingTime} do próximo dia útil. 
            Nesse tempo, o preço da ação poderá sofrer alterações, afetando o valor necessário para investir.`,
            'Continuar',
            'Cancelar',
        )
            .do((result) => this.trackConfirmInvestmentModal(openTime, new Date(), !result))
            .toPromise();
    }

    /**
     * Confirmar encerramento de investimento planejado (mercado fechado)
     * @param opening abertura (data e hora em formato 2018-01-22 14:00)
     */
    private closePlannedInvestmentBooking(opening: string, closingTime?: string): Promise<boolean> {
        // Localiza para hora local
        const openingTime = this.datePipe.transform(opening, 'HH:mm');

        return this.dialogs.showSimpleModal(
            'icon_info',
            'O Mercado está fechado.',
            `Não é possível concluir esta solicitação agora. <strong>Tente novamente no próximo dia útil de ${openingTime} às ${closingTime}.</strong>`,
            'Entendi',
        )
            .do((result) => false)
            .toPromise();
    }

    /**
     * Confirmar agendamento de investimento
     * @param opening abertura (data e hora em formato 2018-01-22 14:00)
     * @param buy compra?
     */
    private confirmInvestmentBooking(opening: string, buy: boolean): Promise<boolean> {
        // Localiza para hora local
        const openingTime = this.datePipe.transform(opening, 'HH:mm');
        const operation = buy ? 'compra' : 'venda';
        const openTime = new Date();

        return this.dialogs.confirmAction(
            'O mercado está fechado.',
            `Se agendar a ${operation} agora, ela será processada a partir das ${openingTime} do próximo dia útil. 
            Nesse tempo, o preço da ação poderá sofrer alterações, afetando o resultado do investimento.`,
            'Continuar',
            'Cancelar',
        )
            .do((result) => this.trackConfirmInvestmentModal(openTime, new Date(), !result))
            .toPromise();
    }

    /**
     * Retorna a hora local em formato HH:mm
     */
    private getLocalTime(): string {
        const today = new Date();
        const hours = this.text.padStart(`${today.getHours()}`, '0', 2);
        const minutes = this.text.padStart(`${today.getMinutes()}`, '0', 2);

        return `${hours}:${minutes}`;
    }

    /**
     * Trackear evento de modal de confirmação
     * @param openTime hora em que a modal foi aberta
     * @param closeTime hora em que a modal foi fechada
     * @param actionCanceled ação cancelada
     */
    private trackConfirmInvestmentModal(openTime: Date, closeTime: Date, actionCanceled: boolean) {
        const elapsedTime = this.utilities.diffTime(closeTime, openTime, 'second', true);
        const event = new MarketClosedWarningEvent(
            SegmentEvent.MarketClosedWarning, {
            duration: elapsedTime,
            canceled: actionCanceled,
        });
        this.store.dispatch({
            type: TrackingActions.trackEvents,
            payload: event,
        });
    }
}

// tslint:disable-next-line:max-classes-per-file
class Result {
    public market: {
        marketOpen: boolean,
        openingTime: string,
    };
    public confirm: boolean;
}
